/**
 * ParticlePhysics — motion, collision and pile (accumulation) logic for
 * the platformer particle stack, split out of ParticleSystem.js along
 * the physics seam. Every function takes the live ParticleSystem
 * instance (`sys`) first and reads/writes its fields directly — this
 * module is a private half of the system, not a public API.
 *
 *   applyPlayerInteract  player wake/push/attract/repel/swirl steering
 *   integrateCollide     point-vs-tile-grid sweep + dynamic body bounce
 *   parkParticle         land a flake into its column's pile (snow)
 *   recomputeColumn      re-stack a column under the current load
 *   onRestingDeath       drop a dead flake + settle the pile above it
 *   bucketOf             column index for the accumulation stacks
 */

import { clamp, lerp, lerpColor, BASE } from './ParticleConfig.js';

/**
 * Apply the chosen "Player interaction" force to ONE particle for every
 * nearby body (the player). For each body we measure the particle's
 * distance to the body's AABB; within `radius` a linear falloff (1 at
 * the surface → 0 at the edge of reach) scales the effect:
 *
 *   'wake'    Slipstream — steer the particle's velocity toward the
 *             body's own (px/s). Needs movement; a still player does
 *             nothing. Snow/rain get swept along and trail behind.
 *   'push'    Bow-wave — shove the particle OUTWARD from the body,
 *             scaled by how fast the player moves. Needs movement.
 *             Parts a field of leaves / dust as the player runs through.
 *   'attract' Magnet — pull the particle TOWARD the body. Works while
 *             the player stands still. Sparks/coins drawn to the hero.
 *   'repel'   Force-field — push the particle AWAY from the body. Works
 *             while still. A bubble that keeps particles off the player.
 *   'swirl'   Orbit — push the particle along the tangent so it circles
 *             the body. Works while still. Embers/petals swirling.
 *
 * `strength` (0..20) is a generic intensity; for the positional modes it
 * maps to acceleration (≈ strength·60 px/s² at the surface), for 'wake'
 * it is a convergence rate (1/s) clamped so it can't overshoot.
 */
export function applyPlayerInteract(p, dt, bodies, mode, strength, radius) {
    for (let i = 0; i < bodies.length; i++) {
        const b = bodies[i];
        if (!b) continue;
        // Closest point on the body box → particle offset + distance.
        const cx = clamp(p.x, b.x, b.x + b.w);
        const cy = clamp(p.y, b.y, b.y + b.h);
        let dx = p.x - cx, dy = p.y - cy;
        let d = Math.sqrt(dx * dx + dy * dy);
        if (d >= radius) continue;
        const fall = 1 - d / radius;                 // 1 at surface → 0 at reach

        if (mode === 'wake') {
            const bvx = b.vx || 0, bvy = b.vy || 0;
            if (bvx * bvx + bvy * bvy < 25) continue; // still player → nothing
            const k = Math.min(1, strength * fall * dt);
            p.vx += (bvx - p.vx) * k;
            p.vy += (bvy - p.vy) * k;
            continue;
        }

        // Outward unit normal (particle relative to the body). When the
        // particle sits inside the box (d≈0) fall back to the player's
        // heading, else straight up, so the force still has a direction.
        if (d < 1e-3) {
            const bvx = b.vx || 0, bvy = b.vy || 0;
            const bs = Math.sqrt(bvx * bvx + bvy * bvy);
            if (bs > 1e-3) { dx = bvx / bs; dy = bvy / bs; } else { dx = 0; dy = -1; }
            d = 1;
        }
        const nx = dx / d, ny = dy / d;
        const accel = strength * 60 * fall;          // px/s² at the surface

        if (mode === 'repel') {
            p.vx += nx * accel * dt;
            p.vy += ny * accel * dt;
        } else if (mode === 'attract') {
            p.vx -= nx * accel * dt;
            p.vy -= ny * accel * dt;
        } else if (mode === 'swirl') {
            // Tangent (−ny, nx) = counter-clockwise orbit around the body.
            p.vx += -ny * accel * dt;
            p.vy += nx * accel * dt;
        } else if (mode === 'push') {
            const bvx = b.vx || 0, bvy = b.vy || 0;
            const bs = Math.sqrt(bvx * bvx + bvy * bvy);
            if (bs < 5) continue;                     // bow-wave needs movement
            const boost = Math.min(3, bs / 200);      // faster player → harder shove
            p.vx += nx * accel * boost * dt;
            p.vy += ny * accel * boost * dt;
        }
    }
}

/**
 * Move ONE particle by (vx,vy)·dt while resolving collisions — a
 * point-sized version of the player's "separated axis" sweep:
 * advance X, snap out of any solid cell it entered (and reflect vx
 * by `bounce`), then the same on Y. One-way platforms catch a piece
 * only when it crosses their top edge falling, just like the player.
 * Finally bounce off any dynamic `bodies` (the player). All lookups
 * are O(1) integer cell hits, so this stays cheap.
 *
 * Returns 'rest' when the particle parked into a pile, 'dead' when its
 * column is full (caller retires it), false when it kept moving.
 */
export function integrateCollide(sys, p, dt, cs, solidAt, oneWayAt, bounce, bodies) {
    const accumulate = sys.cfg.accumulate;

    // ── X axis ──
    const nx = p.x + p.vx * dt;
    const nxCell = Math.floor(nx / cs);
    if (solidAt(nxCell, Math.floor(p.y / cs))) {
        // Snap to the near edge of the cell it tried to enter, reflect.
        p.x = p.vx > 0 ? nxCell * cs - 0.01 : (nxCell + 1) * cs + 0.01;
        p.vx = -p.vx * bounce;
    } else {
        p.x = nx;
    }

    // ── Y axis ──
    const ny = p.y + p.vy * dt;

    // Pile surface first: a falling flake rests on TOP of any flakes
    // already stacked in its column (the heap grows upward) instead of
    // sinking to the bare floor. Parking keeps the flake alive as a
    // real, settled particle ('rest'); a full column retires it ('dead').
    if (accumulate && p.vy > 0 && sys._stacks) {
        const e = sys._stacks.get(bucketOf(sys, p.x));
        if (e && ny >= e.surfaceY) {
            return parkParticle(sys, p, e.floorY) ? 'rest' : 'dead';
        }
    }

    const nyCell = Math.floor(ny / cs);
    let landed = false;
    if (solidAt(Math.floor(p.x / cs), nyCell)) {
        if (p.vy > 0) {
            // Landed on solid ground — park (start a pile here) or
            // settle/bounce, depending on the mode.
            if (accumulate) return parkParticle(sys, p, nyCell * cs) ? 'rest' : 'dead';
            p.y = nyCell * cs - 0.01; landed = true;
        } else {
            p.y = (nyCell + 1) * cs + 0.01;
        }
        p.vy = -p.vy * bounce;
    } else if (p.vy > 0 && oneWayAt) {
        // One-way tops: only land when crossing the boundary downward.
        const prevR = Math.floor(p.y / cs);
        if (nyCell > prevR && oneWayAt(Math.floor(p.x / cs), nyCell)) {
            if (accumulate) return parkParticle(sys, p, nyCell * cs) ? 'rest' : 'dead';
            p.y = nyCell * cs - 0.01;
            p.vy = -p.vy * bounce;
            landed = true;
        } else {
            p.y = ny;
        }
    } else {
        p.y = ny;
    }

    // Resting friction + settle so confetti piles up instead of
    // sliding or jittering on the surface forever.
    if (landed) {
        p.vx *= 0.82;
        if (Math.abs(p.vy) < 8) p.vy = 0;
    }

    // ── Dynamic bodies (the player) — point-vs-AABB push-out ──
    if (bodies) {
        for (let i = 0; i < bodies.length; i++) {
            const b = bodies[i];
            if (!b) continue;
            if (p.x > b.x && p.x < b.x + b.w && p.y > b.y && p.y < b.y + b.h) {
                // Eject along the shallowest penetration axis, reflect.
                const dl = p.x - b.x, dr = (b.x + b.w) - p.x;
                const dt2 = p.y - b.y, db = (b.y + b.h) - p.y;
                const m = Math.min(dl, dr, dt2, db);
                if (m === dt2)      { p.y = b.y - 0.01;        p.vy = -Math.abs(p.vy) * bounce; p.vx *= 0.82; }
                else if (m === db)  { p.y = b.y + b.h + 0.01;  p.vy =  Math.abs(p.vy) * bounce; }
                else if (m === dl)  { p.x = b.x - 0.01;        p.vx = -Math.abs(p.vx) * bounce; }
                else                { p.x = b.x + b.w + 0.01;  p.vx =  Math.abs(p.vx) * bounce; }
            }
        }
    }
    return false;
}

/** Column index for the accumulation stacks. */
export function bucketOf(sys, x) { return Math.floor(x / sys._accumBucket); }

/**
 * Park a landed flake into its column's stack so it becomes a real,
 * settled particle in the pile (it keeps rendering as its own shape —
 * no flat fill). `floorY` is the solid surface the column sits on
 * (seeds a fresh column). The flake rests with its bottom on the
 * current surface; the surface then rises by ~its radius so the NEXT
 * flake lands on top of it. Its look (size, colour, alpha, rotation)
 * is frozen at landing. Returns false when the column has reached
 * `accumMax` thickness (caller retires the flake → fixed max height).
 */
export function parkParticle(sys, p, floorY) {
    if (!sys._stacks) sys._stacks = new Map();
    const b = bucketOf(sys, p.x);
    let e = sys._stacks.get(b);
    if (!e) { e = { floorY, surfaceY: floorY, items: [] }; sys._stacks.set(b, e); }
    if (e.floorY - e.surfaceY >= sys.cfg.accumMax) return false;

    const cfg = sys.cfg;
    const lifeT = p.age / p.life;
    const size = Math.max(1, lerp(cfg.startSize, cfg.endSize, lifeT) * p.sizeMul);
    // Uncompressed vertical claim. Packed a little (0.8) so flakes nestle
    // into each other; `accumWeight` compresses it further once flakes
    // pile on top (see `recomputeColumn`).
    p.baseThickness = Math.max(1.5, size * 0.8);

    p.vx = 0; p.vy = 0;
    p.resting = true;
    p.bucket = b;
    e.items.push(p);                // bottom → top (oldest → newest)
    // Recompute the whole column under the current load: this sets every
    // flake's `restTargetY` (incl. this one) and the new surface. The
    // flakes below get slightly lower targets (compaction) and EASE down
    // smoothly via the resting fast-path; the new flake lands crisply.
    recomputeColumn(sys, e);

    // Freeze the settled look. The new flake snaps to its target (it just
    // fell there); everything below it animates.
    const node = p.node;
    const isImage = p.shape === 'image';
    node.scale.set(size / (isImage ? sys._texRefDim : BASE));
    node.tint = isImage ? 0xffffff
        : (p.shape === 'confetti' ? p.color : lerpColor(cfg.startColor, cfg.endColor, lifeT));
    node.rotation = p.shape === 'spark' ? 0 : p.rot;
    p.restAlpha = clamp(lerp(cfg.startAlpha, cfg.endAlpha, lifeT), 0.15, 1);
    node.alpha = p.restAlpha;
    node.x = p.x;
    p.y = p.restTargetY;
    node.y = p.restTargetY;
    node.visible = true;
    return true;
}

/**
 * Recompute a column's resting layout from the floor up. Each flake
 * claims a slice of height equal to its `baseThickness` COMPRESSED by the
 * load resting on top of it: with `accumWeight` w, a flake under `above`
 * others keeps a fraction `1 − w·above/(above+3)` of its height (→ 1 when
 * unloaded, → 1−w when buried deep). Deeper flakes therefore pack tighter,
 * so the pile reads as a denser mass and sinks as it grows. Only the
 * targets (`restTargetY`) are set here; the per-frame fast-path eases the
 * nodes toward them. O(column size); runs on park + death, not per frame.
 */
export function recomputeColumn(sys, e) {
    const w = sys.cfg.accumWeight || 0;
    const items = e.items;
    const n = items.length;
    let y = e.floorY;               // bottom of the column (screen-down)
    for (let i = 0; i < n; i++) {
        const q = items[i];
        const above = n - 1 - i;    // flakes resting on top of q
        const comp = 1 - w * (above / (above + 3));
        const t = Math.max(1, q.baseThickness * comp);
        q.restThickness = t;
        q.restTargetY = y - t / 2;  // centred in its slice (y grows down)
        y -= t;                     // next flake sits on top
    }
    e.surfaceY = y;
}

/**
 * A resting flake reached the end of its life and died. Drop it from its
 * column and recompute the column so every flake that was above it gets a
 * new (lower) target; the resting fast-path then EASES them down so the
 * heap melts smoothly instead of teleporting a slot. Deaths run
 * oldest-first (the bottom of each column), so the pile lowers from the
 * base and holds a roughly fixed thickness once spawning + dying balance.
 */
export function onRestingDeath(sys, p) {
    p.resting = false;
    if (!sys._stacks) return;
    const e = sys._stacks.get(p.bucket);
    if (!e) return;
    const idx = e.items.indexOf(p);
    if (idx === -1) return;
    e.items.splice(idx, 1);
    if (e.items.length === 0) { sys._stacks.delete(p.bucket); return; }
    recomputeColumn(sys, e);
}
